import moment from "moment";
import pdfMake from "pdfmake/build/pdfmake";
import pdfFonts from "pdfmake/build/vfs_fonts";
import {
  texto,
  identificador,
  numero,
  moneda,
  fecha,
  totalizar,
  construirHojaTabla,
  limpiarNombreArchivo,
  paraPdf,
  descargarLibro,
} from "../../../../Components/ExcelTablaReporte";
import reporteFiscalPdf from "../../../../Pdf/reporteFiscalPdf";

pdfMake.vfs = pdfFonts.pdfMake.vfs;

/**
 * Excel y PDF de los reportes de conversion (Cotizacion Vs Ventas y Pedido Vs Ventas).
 *
 * Una sola tabla por hoja, armada una vez, alimenta los dos archivos para que no puedan decir
 * cosas distintas (mismo patron que `ReporteVentasPorLote/exportarVentasPorLote.js`).
 *
 * Los dos reportes comparten forma exacta: lo unico que cambia es la palabra "Cotizado" contra
 * "Pedido" y los titulos, que llegan en `config`.
 */

/** Un renglon con alerta se resalta: su monto convertido no es de fiar y hay que revisarlo. */
const RELLENO_ALERTA = "#FFF1D6";

const ETIQUETA_ALERTA = {
  sinConsumoRegistrado: "Sin consumo registrado",
  renglonCantidadCero: "Renglon con cantidad cero",
};

// Los `anchoPdf` de cada tabla tienen que sumar <= 748 pt (LETTER horizontal menos margenes,
// `ANCHO_UTIL` en `Pdf/reporteFiscalPdf.js`), o la tabla se sale de la hoja.
// Resumen: 230+56+56+76+76+76+48+76+48 = 742.
const columnasResumen = (config) => [
  { titulo: "Proyecto", tipo: "texto", ancho: 34, anchoPdf: 230 },
  { titulo: "Documentos", tipo: "numero", ancho: 12, anchoPdf: 56 },
  { titulo: "Convertidos", tipo: "numero", ancho: 12, anchoPdf: 56 },
  { titulo: `${config.etiquetaMonto} MXN`, tipo: "moneda", ancho: 18, anchoPdf: 76 },
  { titulo: "Convertido MXN", tipo: "moneda", ancho: 18, anchoPdf: 76 },
  { titulo: "Pendiente MXN", tipo: "moneda", ancho: 18, anchoPdf: 76 },
  { titulo: "% Conv.", tipo: "numero", ancho: 10, anchoPdf: 48 },
  { titulo: "Facturado MXN", tipo: "moneda", ancho: 18, anchoPdf: 76 },
  { titulo: "Ventas", tipo: "numero", ancho: 10, anchoPdf: 48 },
];

export const construirTablaResumen = (resumen, config) => {
  const columnas = columnasResumen(config);
  const filas = (resumen || []).map((grupo) => [
    texto(grupo.proyecto),
    numero(grupo.documentos),
    numero(grupo.documentosConvertidos),
    moneda(grupo.cotizadoMxn),
    moneda(grupo.convertidoMxn),
    moneda(grupo.pendienteMxn),
    numero(grupo.porcentajeConversion),
    moneda(grupo.facturadoMxn),
    numero(grupo.ventasDistintas),
  ]);

  // Aqui `totalizar` sirve tal cual: las cuatro columnas de dinero son todas aditivas y el
  // "% Conv." es de tipo numero, asi que se queda en null por si solo.
  return { columnas, filas, totales: totalizar(columnas, filas) };
};

// Detalle: 86+56+50+96+46+70+70+70+42+70+78 = 734 (<= 748).
const columnasDetalle = (config) => [
  { titulo: "Proyecto", tipo: "texto", ancho: 26, anchoPdf: 86 },
  { titulo: "Folio", tipo: "texto", ancho: 16, anchoPdf: 56 },
  { titulo: "Fecha", tipo: "fecha", ancho: 12, anchoPdf: 50 },
  { titulo: "Cliente", tipo: "texto", ancho: 30, anchoPdf: 96 },
  { titulo: "Vendedor", tipo: "texto", ancho: 24, enPdf: false },
  { titulo: "Estado", tipo: "texto", ancho: 12, anchoPdf: 46 },
  { titulo: "Moneda", tipo: "texto", ancho: 9, enPdf: false },
  // Los importes en la moneda del documento no van al PDF: no son sumables entre si y el
  // ancho de LETTER horizontal no alcanza para las dos parejas de columnas.
  { titulo: config.etiquetaMonto, tipo: "moneda", ancho: 16, enPdf: false },
  { titulo: "Convertido", tipo: "moneda", ancho: 16, enPdf: false },
  { titulo: `${config.etiquetaMonto} MXN`, tipo: "moneda", ancho: 18, anchoPdf: 70 },
  { titulo: "Convertido MXN", tipo: "moneda", ancho: 18, anchoPdf: 70 },
  { titulo: "Pendiente MXN", tipo: "moneda", ancho: 18, anchoPdf: 70 },
  { titulo: "% Conv.", tipo: "numero", ancho: 10, anchoPdf: 42 },
  { titulo: "Facturado MXN", tipo: "moneda", ancho: 18, anchoPdf: 70 },
  { titulo: "Ventas", tipo: "texto", ancho: 26, anchoPdf: 78 },
  { titulo: "Alerta", tipo: "texto", ancho: 22, enPdf: false },
];

export const construirTablaDetalle = (filas, config) => {
  const columnas = columnasDetalle(config);

  const cuerpo = (filas || []).map((fila) => {
    const celdas = [
      texto(fila.proyecto),
      texto(fila.folio),
      fecha(fila.fechaOrden),
      texto(fila.cliente),
      texto(fila.vendedor),
      texto(fila.estado),
      texto(fila.moneda),
      moneda(fila.cotizado),
      moneda(fila.convertido),
      moneda(fila.cotizadoMxn),
      moneda(fila.convertidoMxn),
      moneda(fila.pendienteMxn),
      numero(fila.porcentajeConversion),
      moneda(fila.facturadoMxn),
      texto(fila.ventas),
      texto(ETIQUETA_ALERTA[fila.alerta] || ""),
    ];

    // `fillColor` lo pinta el PDF y el Excel lo ignora; `resalte` es al reves, y ademas conserva
    // el numFmt de Contabilidad en las celdas de dinero. Hacen falta los dos.
    return fila.alerta
      ? celdas.map((celda) => ({ ...celda, fillColor: RELLENO_ALERTA, resalte: "equivalenciaA" }))
      : celdas;
  });

  // Los totales se arman a mano y NO con `totalizar` a secas, por dos razones:
  //  - los importes en la moneda del documento mezclan monedas y sumarlos no significa nada;
  //  - "Facturado MXN" duplica cuando una misma venta cuelga de dos documentos, asi que la cifra
  //    buena es la del resumen por proyecto (ahi va deduplicada) y aqui se deja sin total.
  const todos = totalizar(columnas, cuerpo);
  const totales = columnas.map((columna, indice) =>
    [`${config.etiquetaMonto} MXN`, "Convertido MXN", "Pendiente MXN"].includes(columna.titulo)
      ? todos[indice]
      : null
  );

  return { columnas, filas: cuerpo, totales };
};

// Resumen por insumo: 70+200+50+40+56+56+76+76+76+44 = 744 (<= 748).
const columnasInsumos = (config) => [
  { titulo: "Clave", tipo: "identificador", ancho: 16, anchoPdf: 70 },
  { titulo: "Descripcion", tipo: "texto", ancho: 40, anchoPdf: 200 },
  { titulo: "Unidad", tipo: "texto", ancho: 12, anchoPdf: 50 },
  { titulo: "Documentos", tipo: "numero", ancho: 12, anchoPdf: 40 },
  { titulo: "Convertidos", tipo: "numero", ancho: 12, enPdf: false },
  { titulo: config.etiquetaCantidad, tipo: "numero", ancho: 14, anchoPdf: 56 },
  { titulo: "Cant. vendida", tipo: "numero", ancho: 14, anchoPdf: 56 },
  { titulo: "Cant. pendiente", tipo: "numero", ancho: 14, enPdf: false },
  { titulo: `${config.etiquetaMonto} MXN`, tipo: "moneda", ancho: 18, anchoPdf: 76 },
  { titulo: "Convertido MXN", tipo: "moneda", ancho: 18, anchoPdf: 76 },
  { titulo: "Pendiente MXN", tipo: "moneda", ancho: 18, anchoPdf: 76 },
  { titulo: "% Conv.", tipo: "numero", ancho: 10, anchoPdf: 44 },
];

/**
 * Una fila por insumo. Una equivalencia sale en su propio renglon (otra unidad), y no hay
 * columna de facturado: el subtotal de una venta no se reparte entre insumos.
 */
export const construirTablaInsumos = (resumenInsumos, config) => {
  const columnas = columnasInsumos(config);
  const filas = (resumenInsumos || []).map((insumo) => [
    identificador(insumo.clave),
    texto(insumo.descripcion),
    texto(insumo.unidad),
    numero(insumo.documentos),
    numero(insumo.documentosConvertidos),
    numero(insumo.cantidad),
    numero(insumo.cantidadConvertida),
    numero(insumo.cantidadPendiente),
    moneda(insumo.cotizadoMxn),
    moneda(insumo.convertidoMxn),
    moneda(insumo.pendienteMxn),
    numero(insumo.porcentajeConversion),
  ]);

  // Todos los importes estan en MXN, asi que `totalizar` sirve tal cual.
  return { columnas, filas, totales: totalizar(columnas, filas) };
};

/** Solo Excel: una fila por renglon de cada documento. */
const columnasRenglones = (config) => [
  { titulo: "Proyecto", tipo: "texto", ancho: 26 },
  { titulo: "Folio", tipo: "texto", ancho: 16 },
  { titulo: "Fecha", tipo: "fecha", ancho: 12 },
  { titulo: "Cliente", tipo: "texto", ancho: 30 },
  { titulo: "Estado", tipo: "texto", ancho: 12 },
  { titulo: "Clave", tipo: "identificador", ancho: 16 },
  { titulo: "Descripcion", tipo: "texto", ancho: 40 },
  { titulo: "Unidad", tipo: "texto", ancho: 12 },
  { titulo: config.etiquetaCantidad, tipo: "numero", ancho: 14 },
  { titulo: "Cant. vendida", tipo: "numero", ancho: 14 },
  { titulo: "Cant. pendiente", tipo: "numero", ancho: 14 },
  { titulo: `${config.etiquetaMonto} MXN`, tipo: "moneda", ancho: 18 },
  { titulo: "Convertido MXN", tipo: "moneda", ancho: 18 },
  { titulo: "Pendiente MXN", tipo: "moneda", ancho: 18 },
  { titulo: "% Conv.", tipo: "numero", ancho: 10 },
];

/**
 * `mapaInsumos` (insumoId -> fila de `resumenInsumos`) pone clave, descripcion y unidad: el
 * backend no las repite en cada renglon.
 */
export const construirTablaRenglones = (filas, mapaInsumos, config) => {
  const columnas = columnasRenglones(config);
  const cuerpo = [];
  for (const fila of filas || []) {
    for (const renglon of fila.renglones || []) {
      const insumo = (mapaInsumos && mapaInsumos.get(renglon.insumoId)) || {};
      cuerpo.push([
        texto(fila.proyecto),
        texto(fila.folio),
        fecha(fila.fechaOrden),
        texto(fila.cliente),
        texto(fila.estado),
        identificador(insumo.clave),
        texto(insumo.descripcion),
        texto(insumo.unidad),
        numero(renglon.cantidad),
        numero(renglon.cantidadConvertida),
        numero(renglon.cantidadPendiente),
        moneda(renglon.cotizadoMxn),
        moneda(renglon.convertidoMxn),
        moneda(renglon.pendienteMxn),
        numero(renglon.porcentajeConversion),
      ]);
    }
  }
  return { columnas, filas: cuerpo, totales: totalizar(columnas, cuerpo) };
};

const nombreArchivo = (config, empresaNombre) =>
  `${config.nombreArchivo}_${limpiarNombreArchivo(empresaNombre)}_${moment().format("YYYY-MM-DD")}`;

const dinero = (valor) =>
  `$${(valor || 0).toLocaleString("es-MX", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

const indicadoresDe = (totales = {}, config) => [
  { etiqueta: config.plural, valor: String(totales.documentos || 0) },
  { etiqueta: "Convertidos", valor: String(totales.documentosConvertidos || 0) },
  { etiqueta: config.etiquetaMonto, valor: dinero(totales.cotizadoMxn) },
  { etiqueta: "Convertido", valor: dinero(totales.convertidoMxn) },
  {
    etiqueta: "% Conversion",
    valor: totales.porcentajeConversion != null ? `${totales.porcentajeConversion}%` : "-",
  },
];

export const descargarExcel = (
  { datos, resumen, resumenInsumos, mapaInsumos, empresaNombre, descripcionFiltros },
  config
) => {
  descargarLibro(
    [
      {
        nombre: "Resumen por proyecto",
        hoja: construirHojaTabla(construirTablaResumen(resumen, config), {
          titulo: `${config.titulo} - Resumen por proyecto`,
          empresaNombre,
          descripcionFiltros,
        }),
      },
      {
        nombre: "Resumen por insumo",
        hoja: construirHojaTabla(construirTablaInsumos(resumenInsumos, config), {
          titulo: `${config.titulo} - Resumen por insumo`,
          empresaNombre,
          descripcionFiltros,
        }),
      },
      {
        nombre: "Detalle",
        hoja: construirHojaTabla(construirTablaDetalle(datos, config), {
          titulo: `${config.titulo} - Detalle por documento`,
          empresaNombre,
          descripcionFiltros,
        }),
      },
      {
        nombre: "Detalle por renglon",
        hoja: construirHojaTabla(construirTablaRenglones(datos, mapaInsumos, config), {
          titulo: `${config.titulo} - Detalle por renglon`,
          empresaNombre,
          descripcionFiltros,
        }),
      },
    ],
    nombreArchivo(config, empresaNombre)
  );
};

export const descargarPdf = (
  { datos, resumen, resumenInsumos, totales, empresaNombre, descripcionFiltros },
  config
) => {
  const tablaResumen = construirTablaResumen(resumen, config);
  const tablaInsumos = construirTablaInsumos(resumenInsumos, config);
  const tablaDetalle = construirTablaDetalle(datos, config);

  pdfMake
    .createPdf(
      reporteFiscalPdf({
        titulo: `Reporte ${config.titulo}`,
        empresaNombre,
        descripcionFiltros,
        indicadores: indicadoresDe(totales, config),
        contador: tablaDetalle.filas.length,
        secciones: [
          { titulo: "Resumen por proyecto", ...paraPdf(tablaResumen) },
          { titulo: "Resumen por insumo", ...paraPdf(tablaInsumos) },
          { titulo: "Detalle por documento", ...paraPdf(tablaDetalle) },
        ],
      })
    )
    .download(`${nombreArchivo(config, empresaNombre)}.pdf`);
};
